import { Box, Divider, MenuItem, Popper } from '@mui/material';
import Paper from '@mui/material/Paper';
import { withStyles, WithStyles } from "@mui/styles";
import { InputAdormentStyles } from 'assets/jss/mui-extras/inputAdormentStyle';
import { InputColorStyles } from 'assets/jss/mui-extras/inputColorStyle';
import classNames from "classnames";
import ReactAutoCompleteFallbackDialog from 'components/AutoComplete/ReactAutoCompleteFallbackDialog';
import { ReactAutoCompleteStyles } from 'components/AutoComplete/ReactAutoCompleteStyles';
import { SpinnerIndicator } from 'components/CallServer';
import { RequestErrorReport } from 'components/CallServer/RequestErrorReport';
import { AccessKeys } from 'Constants';
import Downshift, { ControllerStateAndHelpers, StateChangeOptions } from 'downshift';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { ReactAutoCompleteProps } from './ReactAutoCompleteProps';
import { AutoCompleteChipColors, RenderAutoCompleteChip } from './RenderAutoCompleteChip';
import { RenderInput } from './RenderInput';
import RenderSuggestion from './RenderSuggestion';

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface IState {
    inputValue: string;
    lastError: any;
    loading: boolean;
    autoCompleteItems: Array<AutoCompleteDataModel>;
    suggestionLoadMore: boolean;
    searchSkipRecords: number;
    isOpen: boolean;
    dialog: any;
}

interface IProps {
    chipColor?: AutoCompleteChipColors;
}

type Props = ReactAutoCompleteProps
    & WithStyles<typeof ReactAutoCompleteStyles>
    & WithStyles<typeof InputAdormentStyles>
    & WithStyles<typeof InputColorStyles>
    & IProps
    ;

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export const LoadMoreText: React.FC<{ count: number; }> = ({ count }) => {
    const { t } = useTranslation();
    return t("Common.Filter.LoadMoreNum", { count });
};

const AlternateMethodTitle: React.FC = () => {
    const { t } = useTranslation();
    return t("Components.AutoComplete.AriaAlternativeMethodTitle");
};

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

class ReactAutoCompleteComponent extends React.Component<Props, IState> {

    private popperRef: any;
    private outterRef: any;
    private loadMoreId: string;
    private lastChange: number;
    private timer: NodeJS.Timeout;

    static defaultProps: Partial<ReactAutoCompleteProps> = {
        id: new Date().getTime().toString(),
        inputColor: "inherit",
        underlineColor: "inherit",
        autoFocus: false,
        error: false,
        escapeOnEmpty: false,
        multiple: true,
        readonly: false,
        allowAdd: true,
        required: false,
        maxSuggestionCount: 10,
        alternateMethodAccessKey: AccessKeys.AutoComplete
    };

    ///
    constructor(props: Props) {
        super(props);

        this.loadMoreId = props.id + '-loadmore';

        this.state = {
            inputValue: '',
            loading: false,
            lastError: null,
            autoCompleteItems: [],
            suggestionLoadMore: false,
            searchSkipRecords: 0,
            isOpen: false,
            dialog: null
        };
    }

    ///
    private getInitialSelectedItems() {
        const initialvalue = this.props.selectedItems || [];
        return initialvalue;
    }

    ///
    private canAdd() {
        return !this.props.readonly && this.props.allowAdd;
    }

    ///
    private handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>, highlightedIndex: number) {
        const { inputValue, isOpen } = this.state;
        const selectedItems = this.getInitialSelectedItems();

        if (event.keyCode == 27) {
            if (this.props.onEscape) this.props.onEscape();
        }
        else {
            if (!this.canAdd())
                event.preventDefault();
            else if (event.keyCode == 13) {

                if (inputValue == "" && !isOpen && this.props.escapeOnEmpty) {
                    if (this.props.onEscape) this.props.onEscape();
                }
                else if (this.state.autoCompleteItems.length > 0 && isOpen && highlightedIndex == null) {
                    // highlightedIndex é utilizado para diferenciar o ENTER de um "INPUT" ou "Lista de Itens do Menu".
                    const newItems = this.tryAddItems([this.state.autoCompleteItems[0]]);
                    if (newItems != selectedItems) {
                        this.setState(
                            { inputValue: "", isOpen: false },
                            () => this.props.onChange(newItems)
                        );
                    }
                }

            }
            else if (selectedItems.length && !inputValue.length && event.keyCode === 8) {

                if (selectedItems[0].key != "[]" && !selectedItems[0].fixed) {

                    let i = selectedItems.length - 1;
                    while (i >= 0) {
                        if (selectedItems[i].canDelete !== null && selectedItems[i].canDelete == true) {
                            const compareIndex = i;
                            const newItems = selectedItems.filter((_, index) => index != compareIndex);
                            this.props.onChange(newItems);
                            break;
                        }
                        else
                            i--;
                    }
                }
            }
        }
    }

    ///
    private tryAddItems(items: AutoCompleteDataModel[]) {
        let selectedItems = this.getInitialSelectedItems();

        if (items?.length > 0) {
            items.forEach((item) => {
                if (selectedItems.indexOf(item) === -1) {

                    if (selectedItems.length == 1 && selectedItems[0].key == "[]")
                        selectedItems = [];

                    if (selectedItems.findIndex(p => p.key == item.key) == -1) {
                        if (!this.props.onValidate || this.props.onValidate(item)) {
                            if (this.props.multiple)
                                selectedItems = [...selectedItems, item];
                            else
                                selectedItems = [item];
                        }
                    }

                }
            });
        }

        return selectedItems;
    }

    ///
    private handleInputChange = (event: React.FormEvent<HTMLInputElement>) => {
        if (this.timer) clearTimeout(this.timer);

        const v = (event.target as HTMLInputElement).value;

        this.setState({ inputValue: v });

        this.lastChange = new Date().getTime();

        this.timer = setTimeout(() => {
            if ((new Date().getTime() - this.lastChange) > 500) {
                this.getSuggestions(v, 0);
            }
        }, 500);
    };

    ///
    private handleInputBlur = () => {
        if (this.props.onBlur) this.props.onBlur();
    };

    ///
    private handleDelete = (item: AutoCompleteDataModel) => {
        const newItems = this.getInitialSelectedItems().filter(p => p.key != item.key);
        this.props.onChange(newItems);
    };

    ///
    private getSuggestions(searchText: string, skipRecords: number) {

        this.setState({ loading: true, lastError: null });

        this.props.onGetSuggestions(searchText, skipRecords, this.props.maxSuggestionCount + 1)
            .then((data) => {
                var records = data.slice(0, this.props.maxSuggestionCount);
                this.setState({
                    loading: false,
                    autoCompleteItems: (skipRecords == 0 ? records : [...this.state.autoCompleteItems, ...records]),
                    searchSkipRecords: (skipRecords + records.length),
                    suggestionLoadMore: (data.length > records.length),
                });
            })
            .catch((error) => {
                this.setState({
                    autoCompleteItems: [],
                    loading: false,
                    suggestionLoadMore: false,
                    lastError: error.canceled ? null : error
                });
            });
    }

    ///
    private onStateChange = (changes: StateChangeOptions<AutoCompleteDataModel>, downshiftState: ControllerStateAndHelpers<AutoCompleteDataModel>) => {
        if (changes.hasOwnProperty('isOpen')) {
            let selectionChanged = false;
            let selectedItems = this.getInitialSelectedItems();
            let skipRecords = 0;

            // downshift is saying that isOpen should change, so let's change it...
            this.setState(({ isOpen, inputValue }) => {

                if ((!this.props.multiple && selectedItems.length > 0)                                       // Não permite seleção múltipla
                    || (changes.selectedItem == null && changes.type === Downshift.stateChangeTypes.mouseUp) // Click fora da área de seleção
                    || changes.type === Downshift.stateChangeTypes.keyDownEscape                             // Pressionou ESCAPE
                ) {
                    isOpen = false;
                    inputValue = "";
                }
                else {
                    // Se uma seta para baixo foi pressionada, mantém o downshit aberto.
                    // Caso contrário, verifica se a mudança ocorreu devido clicar fora da área do downshift, então solicitamos o fechamento.
                    isOpen = changes.type === Downshift.stateChangeTypes.keyDownArrowDown || Downshift.stateChangeTypes.touchEnd
                        ? true
                        : changes.isOpen;

                    if (changes.type == Downshift.stateChangeTypes.keyDownEnter || changes.type == Downshift.stateChangeTypes.clickItem) {
                        if (changes.selectedItem.key == this.loadMoreId) {
                            // Um ENTER ocorreu no item 'Carregar mais', então mantém o downshit aberto e carrega mais n sugestões.
                            isOpen = true;
                            skipRecords = this.state.searchSkipRecords;
                        }
                        else {
                            // Um ENTER ou Click ocorreu em uma Sugestão.

                            // Se a sugestão está com HIGHLIGHT, a seleciona.
                            // Caso contrário, ocorreu um ENTER na linha de texto, então seleciona o Primeiro Item da Lista de Sugestões.
                            const newItem = changes.selectedItem
                                ? changes.selectedItem
                                : this.state.autoCompleteItems.length > 0
                                    ? this.state.autoCompleteItems[0]
                                    : null;

                            if (newItem != null) {
                                const newItems = this.tryAddItems([changes.selectedItem]);
                                if (newItems != selectedItems) {
                                    selectedItems = newItems;
                                    selectionChanged = true;
                                }
                                isOpen = false;
                                inputValue = "";
                            }
                        }
                    }
                }

                return { isOpen, inputValue };
            }, () => {

                if (this.state.isOpen) {
                    // Obtém as sugestões
                    this.getSuggestions(downshiftState.inputValue, skipRecords);

                    if (skipRecords > 0) {
                        // Mantem o foco no último item carregado.
                        downshiftState.setHighlightedIndex(skipRecords);
                    }
                }

                if (selectionChanged) {
                    this.props.onChange(selectedItems);
                }
            });
        }
        else if (changes.hasOwnProperty('inputValue') && changes.type == Downshift.stateChangeTypes.changeInput) {
            this.setState({ isOpen: changes.inputValue.length > 0 });
        }
    };

    ///
    public componentWillUnmount() {
        if (this.timer) clearTimeout(this.timer);
    }

    ///
    public render() {
        const { autoFocus, chipColor, classes, container, error, id, inputColor, underlineColor, label, margin, placeholder, readonly, required, overModal, alternateMethodAccessKey, onSelectedItemClick, onResolveAvatar } = this.props;
        const { inputValue, isOpen, lastError, loading, autoCompleteItems } = this.state;
        const selectedItems = this.getInitialSelectedItems();

        const popperClasses = classNames({
            [classes.popperRoot]: true,
            [classes.popperRootOverModal]: overModal || false
        });

        return (
            <div id={id} className={classes.root}>
                {this.state.dialog}

                {
                    !readonly &&
                    <Box display="none">
                        <a
                            accessKey={alternateMethodAccessKey}
                            onClick={
                                () => {
                                    this.setState({
                                        dialog: <ReactAutoCompleteFallbackDialog
                                            title={this.props.label}
                                            maxSuggestionCount={this.props.maxSuggestionCount}
                                            alreadySelectedItems={this.props.selectedItems}
                                            onGetSuggestions={this.props.onGetSuggestions}
                                            onClose={(selectedItems) => {
                                                const newItems = this.tryAddItems(selectedItems);
                                                this.props.onChange(newItems);
                                                this.setState({ dialog: null });
                                            }}
                                        />
                                    });
                                }
                            }
                        >
                            <AlternateMethodTitle />
                        </a>
                    </Box>
                }

                <Downshift
                    inputValue={inputValue}
                    initialSelectedItem={selectedItems[0]}
                    onStateChange={this.onStateChange}
                    isOpen={isOpen}

                >
                    {({
                        getInputProps,
                        getItemProps,
                        getMenuProps,
                        isOpen,
                        inputValue: inputValue2,
                        selectedItem: selectedItem2,
                        highlightedIndex,
                    }) => (
                        <div className={classes.container}>
                            <div ref={(ref) => this.outterRef = ref}>
                                {
                                    RenderInput({
                                        fullWidth: true,
                                        label: label,
                                        error: error,
                                        margin: margin,
                                        required: required,

                                        InputProps: {
                                            ...getInputProps(
                                                {
                                                    onChange: this.handleInputChange,
                                                    onBlur: this.handleInputBlur,
                                                    onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => this.handleKeyDown(event, highlightedIndex),
                                                    placeholder: selectedItems.length == 0 ? placeholder : "",
                                                    autoFocus: autoFocus,
                                                }),
                                            ...{
                                                startAdornment: selectedItems.map((item, index) => (
                                                    <RenderAutoCompleteChip
                                                        key={index}
                                                        item={item}
                                                        className={classes.autocompleteChip}
                                                        color={chipColor}
                                                        onResolveAvatar={onResolveAvatar}
                                                        onClick={onSelectedItemClick}
                                                        onDelete={(!readonly && item.canDelete != null && item.canDelete === true ? this.handleDelete : null)}
                                                    />
                                                )),
                                            },
                                            ...{
                                                classes: {
                                                    root: classes.autocompleteInputRoot + (inputColor ? " " + classes["root_" + inputColor] : ""),
                                                    input: classes.inputInput,
                                                    underline: underlineColor ? classes.under + " " + classes["under_" + underlineColor] : null,
                                                }
                                            }
                                        },
                                        ref: (node) => {
                                            if (node && !this.canAdd()) (node as HTMLInputElement).style.display = "none";
                                            if (this.props.inputRef) this.props.inputRef(node);
                                        },
                                    })
                                }

                                <div ref={(ref) => this.popperRef = ref}>
                                    <div  {...getMenuProps()} >
                                        <Popper
                                            disablePortal={false}
                                            open={isOpen}
                                            anchorEl={this.outterRef}
                                            className={popperClasses}
                                            container={container ? document.getElementById(container) : document.body}
                                        >
                                            <Paper square style={{ width: (this.popperRef ? this.popperRef.clientWidth : null) }} >
                                                {
                                                    lastError
                                                        ? <RequestErrorReport error={lastError} />
                                                        : autoCompleteItems.map((suggestion, index) =>
                                                            <RenderSuggestion
                                                                key={`${id}-${index}`}
                                                                suggestion={suggestion}
                                                                index={index}
                                                                currentInputValue={inputValue}
                                                                itemProps={getItemProps({ item: suggestion })}
                                                                highlightedIndex={highlightedIndex}
                                                                selectedItem={selectedItem2?.label || ""}
                                                            />
                                                        )
                                                }

                                                {
                                                    this.state.suggestionLoadMore &&
                                                    <>
                                                        <Divider />

                                                        <MenuItem
                                                            {...getItemProps({ item: { key: this.loadMoreId, label: "" } })}
                                                            key={this.loadMoreId}
                                                            selected={highlightedIndex == autoCompleteItems.length}
                                                            component="div"
                                                            onClick={() => { this.getSuggestions(this.state.inputValue, this.state.searchSkipRecords); }}
                                                        >
                                                            <LoadMoreText count={this.props.maxSuggestionCount} />
                                                        </MenuItem>
                                                    </>
                                                }

                                                {
                                                    loading && <SpinnerIndicator />
                                                }
                                            </Paper>
                                        </Popper>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )
                    }
                </Downshift>
            </div>
        );
    }
}

const withAllStyles = withStyles(
    (theme) => ({
        ...InputAdormentStyles(theme),
        ...InputColorStyles(theme),
        ...ReactAutoCompleteStyles(theme)
    }),
    { withTheme: true },
);

export default withAllStyles(ReactAutoCompleteComponent);